"use client"

import { Box, Flex, Spinner, Text } from "@chakra-ui/react"
import { neutral, brand, type } from "../../theme/tokens"

/**
 * Occupies the space its content will fill.
 *
 * The report tables rendered a bare `<Spinner />` while loading, which
 * collapsed the panel to a ~24px box in the top-left corner: the page jumped,
 * and it read as an error rather than as work in progress. This holds the
 * frame and says what is happening.
 */
const LoadingBlock = ({ label = "Loading…", minH = "320px", ...rest }) => (
  <Flex direction="column" align="center" justify="center" gap={3} minH={minH} w="100%" {...rest}>
    <Spinner thickness="2px" speed="0.7s" size="md" color={brand[500]} emptyColor={neutral[200]} />
    <Text fontSize={type.size.small} color={neutral[500]}>
      {label}
    </Text>
  </Flex>
)

/** Row-shaped placeholders — closer to the real thing than a spinner. */
export const TableSkeleton = ({ rows = 8, minH = "320px" }) => (
  <Box p={3} minH={minH} w="100%">
    {Array.from({ length: rows }).map((_, i) => (
      <Box
        key={i}
        h="14px"
        mb="10px"
        borderRadius="3px"
        bg={i === 0 ? neutral[200] : neutral[100]}
        w={i === 0 ? "100%" : `${94 - (i % 4) * 7}%`}
        sx={{
          // Compositor-driven, so it keeps animating while the main thread is
          // busy parsing the response it is waiting for.
          willChange: "opacity",
          animation: `skeletonPulse 1.4s ease-in-out ${i * 60}ms infinite`,
          "@keyframes skeletonPulse": {
            "0%, 100%": { opacity: 0.55 },
            "50%": { opacity: 1 },
          },
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }}
      />
    ))}
  </Box>
)

export default LoadingBlock
